import ObjectDataTagger from './ObjectDataTagger';
import SearchInteraction from './events/unstructured/SearchInteraction';
import SearchResultsAdder from './events/adders/SearchResultAdder';

/**
 * @extends ObjectDataTagger
 */
export default class ObjectDataSearchTagger extends ObjectDataTagger {
  constructor(defaultConfig, trackerConfig) {
    super(defaultConfig, trackerConfig);
    this.snowplow.globalContexts.importFromAutoV10(this.snowplow.pendingEvents);
    this.searchInteractionEvent = new SearchInteraction(this.snowplow);
  }

  /**
   * Tagging the vehicle results
   * @param {object} data
   * @param {Array<integer>} data.vehicles - list of vehicles id: [242341, 324234124, 42343442]
   * @param {dictionary<integer>|null} data.vehiclesCountPerGroup - vehicles count per group hash e.g.: { '88e1b14c671ca79759c99aab17fef40e3a6b70b5': 12 }
   * @param {dictionary<integer>|null} data.vehiclesCountPerFranchise - vehicles count per franchise hash e.g.: { '9759c99fef40e388e1b14c671ca7a6b17b70b5aa': 2, 'ab179759c99fef40e388e1b14c671ca7a6b70b5a': 1 }
   * @param {dictionary<integer>|null} data.vehiclesCountPerLocation - vehicles count per location hash e.g.: { 'ef40e3a688e1b14c99fb70b5aa67b171ca79759c': 5, 'e3a688e1b14c671ca79759c99fb70b5aaef40b17': 6 }
   * @param {Array<string>} data.vehicleOfferIds - vehicle offer ids e.g. ['6399O1125608']
   */
  trackVehicleResults(data) {
    SearchResultsAdder.track(
      this.snowplow,
      this.contexts,
      data.vehicles,
      data.vehiclesCountPerGroup,
      data.vehiclesCountPerFranchise,
      data.vehiclesCountPerLocation,
      data.vehicleOfferIds,
    );
  }

  /**
   * Setting active filters
   * <p> Keys in dictionary (which are names of set fields) should be one of these: </p>
   * <table>
   *
   * <tr> <td> 'approval_programs' </td></tr>
   * <tr> <td> 'body_style', 'budget_program' </td></tr>
   * <tr> <td> 'cap_id', 'colour', 'condition', 'condition-tab', 'country', 'custom', 'custom_model' </td></tr>
   * <tr> <td> 'distance' </td></tr>
   * <tr> <td> 'engine_size', 'engine_size_cc', 'entry_type', 'equipment', 'exterior_colour' </td></tr>
   * <tr> <td> 'finance_annual_mileage', 'finance_deposit', 'finance_price', 'finance_price_min', 'finance_price_max', 'finance_term', 'franchise', 'franchise_hash', 'franchise_approved', 'franchise_name', 'fuel_type' </td></tr>
   * <tr> <td> 'gearbox_type', 'group', 'group_hash', 'group_name' </td></tr>
   * <tr> <td> 'insurance_group' </td></tr>
   * <tr> <td> 'keyword', 'keyword_text' </td></tr>
   * <tr> <td> 'location', 'location_hash', 'location_name' </td></tr>
   * <tr> <td> 'make', 'model', 'model_year', 'mileage', 'mpg', 'mpg_combined' </td></tr>
   * <tr> <td> 'ncap_overall', 'num_doors', 'num_images', 'num_owners', 'num_seats' </td></tr>
   * <tr> <td> 'ovs_model', 'ovs_trim', 'ovs_filter' </td></tr>
   * <tr> <td> 'pack_id', 'payment', 'postcode', 'postcode_area', 'previous_price', 'price', 'price_currency', 'price_min', 'price_max' </td></tr>
   * <tr> <td> 'region', 'region_town', 'registered_at' </td></tr>
   * <tr> <td> 'search_variant', 'section', 'section_custom_query' </td></tr>
   * <tr> <td> 'tax_band', 'tax_price', 'town', 'transmission', 'type' </td></tr>
   * <tr> <td> 'usd_price' </td></tr>
   * <tr> <td> 'variant', 'vehicle_type' </td></tr>
   * <tr> <td> 'wheelbase' </td></tr>
   * </table>
   * </p>
   * <p> Although it's also acceptable by snowplow to get custom key like: 'pageid'. </p>
   * <p> Items in array (which are selected values of the field) inside dictionary should be strings or numbers. </p>
   * <p> e.g.: SearchFilters = { 'bodystyle': ['hatckback', 'sport'], 'finance_price_min': [0], 'finance_price_max': [1000] } </p>
   * @param {dictionary<array>} searchFilters
   */
  setSearchFilters(searchFilters) {
    this.searchInteractionEvent.setSearchFilters(searchFilters);
  }

  /**
   * Tagging a field value change
   * @param {string} fieldName - naming convention should be the same like for setSearchFilters keys
   * @param {array} values - value of the changed field e.g: ['hatckback', 'sport'], [1000]
   */
  trackSearchInteraction(fieldName, values) {
    this.searchInteractionEvent.setContexts(this.contexts);
    this.searchInteractionEvent.trackInteraction(fieldName, values);
  }
}
